import { invokeEdgeFunction } from './edgeFunctionUtils';
import { Version } from '@/store/versionsSlice';
import { formatVersionDateUS } from './dateUtils';

/**
 * Fetches versions for a specific company slug
 * @param slug The company slug to fetch versions for
 * @returns An array of versions or an empty array if none found
 */
export const fetchVersions = async (slug: string): Promise<Version[]> => {
  try {
    console.log(`🔄 [versionsHelper] Fetching versions for ${slug}`);
    const response = await invokeEdgeFunction('get-versions', { slug });
    
    if (response.error) {
      console.error(`❌ [versionsHelper] Error fetching versions:`, response.error);
      throw new Error(response.error);
    }
    
    if (!response.versions || !Array.isArray(response.versions)) {
      console.log(`⚠️ [versionsHelper] No versions found for ${slug}`);
      return [];
    }
    
    console.log(`✅ [versionsHelper] Found ${response.versions.length} versions for ${slug}`);
    return response.versions;
  } catch (error) {
    console.error(`❌ [versionsHelper] Error in fetchVersions:`, error);
    return [];
  }
};

/**
 * Formats a version string (YYYYMMDD) to a more readable format
 * @param version The version string to format
 * @returns A formatted date string
 */
export const formatVersion = (version: string): string => {
  const formatted = formatVersionDateUS(version);
  if (formatted) {
    return formatted;
  }
  return version;
};

/**
 * Gets the latest version for a company
 * @param versions Array of versions
 * @returns The latest version or null if no versions
 */
export const getLatestVersion = (versions: Version[]): Version | null => {
  if (!versions || versions.length === 0) return null;
  
  // Sort versions by version string (YYYYMMDD format) in descending order
  const sortedVersions = [...versions].sort((a, b) => {
    return b.version.localeCompare(a.version);
  });
  
  return sortedVersions[0];
};

/**
 * Gets all verified versions
 * @param versions Array of versions
 * @returns Array of verified versions
 */
export const getVerifiedVersions = (versions: Version[]): Version[] => {
  if (!versions || versions.length === 0) return [];
  
  return versions.filter(version => version.verified);
};

/**
 * Gets all versions with changes
 * @param versions Array of versions
 * @returns Array of versions with changes
 */
export const getChangedVersions = (versions: Version[]): Version[] => {
  if (!versions || versions.length === 0) return [];
  
  return versions.filter(version => version.changed);
};
